import { useState } from "react";
import dayjs from "dayjs";
import { BadgeCheck, BellOff, CircleDollarSign, Loader2, MailCheck, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { StatusBadges } from "@/components/StatusBadge";
import { useReviewPayment, useSendReminder, useSetQuote } from "@/hooks/queries";
import { errorMessage } from "@/lib/api";
import { SERVICE_LABELS } from "@/lib/booking";
import { PAYMENT_LABELS, atl, fareLabel, isUpcoming, shortAddress } from "@/lib/format";

function QuoteControl({ booking }) {
  const setQuote = useSetQuote();
  const [fare, setFare] = useState(booking.fare ?? "");
  if (booking.quote_status !== "pending" || booking.status === "cancelled") return null;
  const run = () =>
    setQuote.mutate(
      { id: booking.id, fare: Number(fare) },
      { onSuccess: () => toast.success("Rate confirmed — the client can now pay"), onError: (e) => toast.error(errorMessage(e)) },
    );
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3 text-xs" data-testid={`owner-quote-control-${booking.id}`}>
      <span className="text-mist">
        Custom quote{booking.distance_miles ? ` · ~${booking.distance_miles} mi` : ""}{booking.fare ? ` · suggested ${fareLabel({ fare: booking.fare })}` : ""}
      </span>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="1"
          step="1"
          value={fare}
          onChange={(e) => setFare(e.target.value)}
          placeholder="Rate"
          className="field w-24 px-3 py-2 text-xs"
          data-testid={`owner-quote-input-${booking.id}`}
        />
        <button type="button" onClick={run} disabled={!(Number(fare) > 0) || setQuote.isPending} className="btn-gold px-4 py-2 text-xs" data-testid={`owner-quote-confirm-btn-${booking.id}`}>
          {setQuote.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CircleDollarSign className="h-3.5 w-3.5" />} Confirm rate
        </button>
      </div>
    </div>
  );
}

function ReminderControl({ booking }) {
  const send = useSendReminder();
  const upcoming = isUpcoming(booking);
  const off = booking.client && !booking.client.ride_reminders;
  const run = () =>
    send.mutate(booking.id, {
      onSuccess: () => toast.success(`Reminder sent to ${booking.client.name}`),
      onError: (e) => toast.error(errorMessage(e)),
    });
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3 text-xs">
      <span className="flex items-center gap-1.5 text-mist" data-testid={`owner-booking-reminder-status-${booking.id}`}>
        {booking.reminder_status === "sent" ? (
          <><MailCheck className="h-3.5 w-3.5 text-gold" /> Reminder sent {dayjs(booking.reminder_sent_at).format("MMM D, h:mm A")}</>
        ) : off ? (
          <><BellOff className="h-3.5 w-3.5" /> Client turned reminders off</>
        ) : upcoming ? (
          "Reminder goes out automatically within 24 hours of pickup"
        ) : (
          "—"
        )}
      </span>
      {upcoming && !off && booking.client && (
        <button type="button" onClick={run} disabled={send.isPending} className="btn-ghost px-4 py-2 text-xs" data-testid={`owner-send-reminder-btn-${booking.id}`}>
          {send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 text-gold" />} Send reminder now
        </button>
      )}
    </div>
  );
}

function PaymentReview({ booking }) {
  const review = useReviewPayment();
  if (booking.status === "cancelled" || booking.payment_status === "unpaid") {
    return booking.payment_verification === "not_received" ? (
      <p className="flex items-center gap-1.5 border-t border-line pt-3 text-xs text-cancelled" data-testid={`owner-payment-review-status-${booking.id}`}>
        <XCircle className="h-3.5 w-3.5" /> Marked not received {dayjs(booking.payment_verified_at).format("MMM D, h:mm A")} —{" "}
        {booking.payment_notice_sent_at ? `client emailed ${dayjs(booking.payment_notice_sent_at).format("MMM D, h:mm A")}` : "client asked to pay again"}
      </p>
    ) : null;
  }
  const verified = booking.payment_verification === "verified";
  const method = PAYMENT_LABELS[booking.payment_method];
  const run = (decision) =>
    review.mutate(
      { id: booking.id, decision },
      {
        onSuccess: () => toast.success(decision === "verified" ? "Payment verified" : "Marked as not received — we're emailing the client"),
        onError: (e) => toast.error(errorMessage(e)),
      },
    );
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3 text-xs">
      <span className="flex items-center gap-1.5 text-mist" data-testid={`owner-payment-review-status-${booking.id}`}>
        {verified ? (
          <><BadgeCheck className="h-3.5 w-3.5 text-gold" /> {method} payment verified {dayjs(booking.payment_verified_at).format("MMM D, h:mm A")}</>
        ) : booking.payment_status === "paid" ? (
          `Client says they paid via ${method} — check your ${method} account`
        ) : (
          `Client opened ${method} but hasn't confirmed yet`
        )}
      </span>
      <div className="flex gap-2">
        {!verified && (
          <button type="button" onClick={() => run("verified")} disabled={review.isPending} className="btn-gold px-4 py-2 text-xs" data-testid={`owner-verify-payment-btn-${booking.id}`}>
            {review.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BadgeCheck className="h-3.5 w-3.5" />} Mark verified
          </button>
        )}
        <button type="button" onClick={() => run("not_received")} disabled={review.isPending} className="btn-ghost px-4 py-2 text-xs text-cancelled hover:border-cancelled/50" data-testid={`owner-not-received-btn-${booking.id}`}>
          <XCircle className="h-3.5 w-3.5" /> Not received
        </button>
      </div>
    </div>
  );
}

export function OwnerBookingCard({ booking, index = 0 }) {
  const d = atl(booking.scheduled_at);
  return (
    <div className="lux-card rise space-y-3 p-4 sm:p-5" style={{ animationDelay: `${index * 50}ms` }} data-testid={`owner-booking-card-${booking.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex w-14 shrink-0 flex-col items-center rounded-xl border border-line bg-elevated py-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-mist">{d.format("MMM")}</span>
          <span className="font-display text-2xl leading-none text-ivory">{d.format("D")}</span>
          <span className="mt-1 text-[10px] text-faint">{d.format("h:mm A")}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-ivory" data-testid={`owner-booking-client-${booking.id}`}>
            {booking.client?.name || "Unknown client"}
            {booking.client?.is_deleted && <span className="ml-2 text-[10px] uppercase tracking-widest text-cancelled">Deleted</span>}
          </p>
          <p className="truncate text-xs text-faint">{booking.client?.email}</p>
          <p className="mt-2 truncate text-[11px] uppercase tracking-[0.18em] text-gold">
            {SERVICE_LABELS[booking.service_type]} <span className="text-faint">·</span>{" "}
            <span className="normal-case tracking-normal text-mist">{booking.vehicle_name} · {booking.passengers} pax</span>
          </p>
          <p className="mt-1 truncate text-xs text-mist">
            {shortAddress(booking.pickup.address)} → {booking.dropoff ? shortAddress(booking.dropoff.address) : `${booking.hours} hrs, as directed`}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <span className="font-display text-lg text-ivory" data-testid={`owner-booking-fare-${booking.id}`}>{fareLabel(booking)}</span>
          <StatusBadges booking={booking} testId={`owner-booking-${booking.id}`} />
          {booking.payment_method && <span className="text-[10px] text-faint">{PAYMENT_LABELS[booking.payment_method]}</span>}
        </div>
      </div>
      {booking.notes && <p className="rounded-xl bg-elevated/50 px-3 py-2 text-xs text-mist">“{booking.notes}”</p>}
      <QuoteControl booking={booking} />
      <PaymentReview booking={booking} />
      <ReminderControl booking={booking} />
    </div>
  );
}
